import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    addQuarters,
    addWeeks,
    addYears,
    differenceInDays,
    differenceInHours,
    differenceInMonths,
    differenceInQuarters,
    differenceInWeeks,
    differenceInYears,
    format,
    isValid,
    parse,
    parseISO,
    startOfMonth,
    subDays,
    subHours,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
    toDate
} from 'date-fns';
import { RecurrenceRule } from 'sr-types/lib/production/v1/graphql';
import { MS_PER_HOUR, MULTI_DAY_THRESHOLD_HOURS, TIME_UNITS } from '../../scheduler/schedulerConstants';
import { Measurement } from '../form/widgets/Measurement';
import { failedToastMessage } from './commonUtils';
import { API_TIME_FORMAT, mergeDateAndTime } from './dateTimeValues';

// Preserve existing imports while callers migrate to dateTimeValues.
export { API_TIME_FORMAT, mergeDateAndTime, toDateTimeISOWithoutOffset } from './dateTimeValues';

export type DurationUnit =
    | typeof TIME_UNITS.hour
    | typeof TIME_UNITS.day
    | typeof TIME_UNITS.week
    | typeof TIME_UNITS.month
    | typeof TIME_UNITS.year;

const testDate = new Date(2000, 10, 12, 14, 5, 6);
export const isAMPM = (locale: string): boolean => {
    return testDate.toLocaleTimeString(locale).includes('PM');
};
// export const DISPLAY_DATE_FORMAT = testDate.toLocaleDateString().replace('11', 'MM').replace('12', 'dd').replace('2000', 'yyyy');
export const getDisplayTimeFormat = (locale: string): string => {
    return isAMPM(locale) ? 'hh:mm a' : 'HH:mm';
};
export const getDisplayTimePlaceholder = (locale: string): string => {
    return isAMPM(locale) ? 'h:m a|p(m)' : 'h:m';
};
export const API_DATE_FORMAT = 'yyyyMMdd';
export const API_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";
export const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric'
};

// Create a fresh local-midnight reference date so date-only parsing stays deterministic
// without sharing a mutable Date instance across consumers.
const makeApiDateParseReference = () => new Date(1970, 0, 1);

export const parseApiDate = (value?: string): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const parsedDate = parse(value, API_DATE_FORMAT, makeApiDateParseReference());
    return isValid(parsedDate) ? parsedDate : undefined;
};

export const toApiDateTimeString = (date: Date) => {
    return format(date, API_DATE_TIME_FORMAT);
};

export const toDisplay = (isoDate: string, short: boolean = false, locale) => {
    return isoDate && isoDate !== '' ? toLocaleDateString(parseISO(isoDate), short, locale) : '';
};

export const toLocaleDateString = (date: string | Date, short: boolean = false, locale?: string) => {
    if (date === '') {
        return '';
    }
    const d: Date = typeof date === 'string' ? parseISO(date) : date;
    return d.toLocaleDateString(locale, short ? SHORT_DATE_OPTIONS : {});
};

export const toDisplayDateAndTime = (date, time, showTime, locale) => {
    if (showTime) {
        return toLocaleDateString(date, false, locale) + ' ' + toDisplayLocaleTime(time, locale);
    }
    return toLocaleDateString(date, false, locale);
};

export const toDisplayLocaleDateAndTime = (isoDateTime, showTime, locale) => {
    if (showTime) {
        return isoDateTime.toLocaleString(locale);
    } else {
        return toLocaleDateString(isoDateTime, false, locale);
    }
};

export const toDisplayDateTimeRange = (dateRange: DateRange, locale: string) => {
    if (dateRange.dateTimeDuration && dateRange.dateTimeDuration.unit == 'hour') {
        if (dateRange.start === dateRange.end) {
            return [
                toLocaleDateString(dateRange.start, false, locale) +
                    ' ' +
                    toDisplayLocaleTime(dateRange.startTime, locale),
                toDisplayLocaleTime(dateRange.endTime, locale)
            ].join('-');
        }
        return [
            toLocaleDateString(dateRange.start, false, locale) + ' ' + toDisplayLocaleTime(dateRange.startTime, locale),
            toLocaleDateString(dateRange.end, false, locale) + ' ' + toDisplayLocaleTime(dateRange.endTime, locale)
        ].join('-');
    }
    if (dateRange.start === dateRange.end) {
        return toLocaleDateString(dateRange.start, false, locale);
    }
    return toLocaleDateString(dateRange.start, false, locale) + '-' + toLocaleDateString(dateRange.end, false, locale);
};

export const toDisplayLocaleTime = (time: string, locale) => {
    return time && time !== '' ? format(parse(time, API_TIME_FORMAT, new Date()), getDisplayTimeFormat(locale)) : '';
};

export const toJSDate = (isoDate) => {
    return isoDate && isoDate !== '' ? parseISO(isoDate) : undefined;
};
export const toDisplayTime = (time) => {
    return time && time !== '' ? format(parse(time, API_TIME_FORMAT, new Date()), 'hh:mm a') : '';
};

export const toYearMonthFromISOString = (isoDate: string, locale: string) => {
    return parseISO(isoDate).toLocaleString(locale, { month: 'long', year: 'numeric' }); //=> 'April 2021';
};

export const toYearFromISOString = (isoDate: string, locale) => {
    return parseISO(isoDate).toLocaleString(locale, { year: 'numeric' });
};

export const toISOFromJSDate = (date: Date) => {
    return format(date, API_DATE_FORMAT);
};

export const toSimpleTimeFromJSDate = (date: Date) => {
    return format(date, API_TIME_FORMAT);
};

export const toDateTimeFromJSDate = (date: Date) => {
    return toDate(date);
};

export const toDateTimeInUTC = (date: Date) => {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss'+00:00'");
};

export const isoDateToDisplayTime = (date: string, locale: string) => {
    return format(parseISO(date), getDisplayTimeFormat(locale));
};

export const calculatePeriod = (start: string, end: string, type) => {
    let dur = '-';
    let startDate = type === TimePeriod.Hours ? parse(start, API_TIME_FORMAT, new Date()) : parseISO(start);
    let endDate = type === TimePeriod.Hours ? parse(end, API_TIME_FORMAT, new Date()) : parseISO(end);
    if (startDate !== undefined && endDate != undefined) {
        switch (type) {
            case TimePeriod.Hours:
                dur = differenceInHours(endDate, startDate, { roundingMethod: 'ceil' }).toString();
                break;
            case TimePeriod.Days:
                dur = differenceInDays(endDate, startDate).toString();
                break;
            case TimePeriod.Weeks:
                dur = differenceInWeeks(endDate, startDate, { roundingMethod: 'ceil' }).toString();
                break;
            case TimePeriod.Months:
                dur = differenceInMonths(endDate, startDate).toString();
                break;
            case TimePeriod.Quarters:
                dur = differenceInQuarters(endDate, startDate, { roundingMethod: 'ceil' }).toString();
                break;
            case TimePeriod.Years:
                dur = differenceInYears(endDate, startDate).toString();
                break;
            default:
                dur = differenceInDays(endDate, startDate).toString();
                break;
        }
    }
    return dur;
};

export const addPeriod = (start, period, type) => {
    let startDate = type === TimePeriod.Hours ? parse(start, API_TIME_FORMAT, new Date()) : parseISO(start);
    let endDate = startDate;
    if (startDate !== undefined && period != undefined) {
        switch (type) {
            case TimePeriod.Hours:
                endDate = addHours(startDate, parseInt(period));
                break;
            case TimePeriod.Days:
                endDate = addDays(startDate, parseInt(period));
                break;
            case TimePeriod.Weeks:
                endDate = addWeeks(startDate, parseInt(period));
                break;
            case TimePeriod.Months:
                endDate = addMonths(startDate, parseInt(period));
                break;
            case TimePeriod.Quarters:
                endDate = addQuarters(startDate, parseInt(period));
                break;
            case TimePeriod.Years:
                endDate = addYears(startDate, parseInt(period));
                break;
            default:
                endDate = addDays(startDate, parseInt(period));
                break;
        }
    }
    return format(endDate, API_DATE_FORMAT);
};

export const subtractPeriod = (end, period, type) => {
    let endDate = type === TimePeriod.Hours ? parse(end, API_TIME_FORMAT, new Date()) : parseISO(end);
    let startDate = endDate;
    if (startDate !== undefined && period != undefined) {
        switch (type) {
            case TimePeriod.Hours:
                startDate = subHours(endDate, parseInt(period));
                break;
            case TimePeriod.Days:
                startDate = subDays(endDate, parseInt(period));
                break;
            case TimePeriod.Weeks:
                startDate = subWeeks(endDate, parseInt(period));
                break;
            case TimePeriod.Months:
                startDate = subMonths(endDate, parseInt(period));
                break;
            case TimePeriod.Quarters:
                startDate = subQuarters(endDate, parseInt(period));
                break;
            case TimePeriod.Years:
                startDate = subYears(endDate, parseInt(period));
                break;
            default:
                startDate = subDays(endDate, parseInt(period));
                break;
        }
    }
    return format(startDate, API_DATE_FORMAT);
};

export const durationToHoursMinutes = (dur) => {
    return format(addMinutes(new Date(0), dur), 'hh:mm');
};

export const calculateDifference = (start, end, unit) => {
    let dur = 0;
    if (start && isValid(start) && end && isValid(end) && unit) {
        if (unit === 'day') {
            end = addDays(end, 1);
        }
        switch (unit) {
            case 'hour':
                dur = differenceInHours(end, start, { roundingMethod: 'ceil' });
                break;
            case 'day':
                dur = differenceInDays(end, start);
                break;
            case 'week':
                dur = differenceInWeeks(end, start, { roundingMethod: 'ceil' });
                break;
            case 'month':
                dur = differenceInMonths(end, start);
                break;
            case 'quarter':
                dur = differenceInQuarters(end, start, { roundingMethod: 'ceil' });
                break;
            case 'year':
                dur = differenceInYears(end, start);
                break;
            default:
                dur = differenceInDays(end, start);
        }
    }
    return dur;
};

export enum TimePeriod {
    Hours = 'Hours',
    Days = 'Days',
    Weeks = 'Weeks',
    Months = 'Months',
    Quarters = 'Quarters',
    Years = 'Years'
}

export const UNIT_MAP: Record<string, { period: TimePeriod; unit: DurationUnit }> = {
    hours: { period: TimePeriod.Hours, unit: TIME_UNITS.hour },
    hour: { period: TimePeriod.Hours, unit: TIME_UNITS.hour },
    days: { period: TimePeriod.Days, unit: TIME_UNITS.day },
    day: { period: TimePeriod.Days, unit: TIME_UNITS.day },
    weeks: { period: TimePeriod.Weeks, unit: TIME_UNITS.week },
    week: { period: TimePeriod.Weeks, unit: TIME_UNITS.week },
    months: { period: TimePeriod.Months, unit: TIME_UNITS.month },
    month: { period: TimePeriod.Months, unit: TIME_UNITS.month },
    years: { period: TimePeriod.Years, unit: TIME_UNITS.year },
    year: { period: TimePeriod.Years, unit: TIME_UNITS.year }
};

export const splitDateAndTime = (date) => {
    return [format(date, API_DATE_FORMAT), format(date, API_TIME_FORMAT)];
};

export interface DateRange {
    start: string;
    startTime?: string;
    end: string;
    endTime?: string;
    duration: string;
    durationType?: string;
    recurrenceRule?: RecurrenceRule;
    dateTimeDuration?: Measurement;
    durationSpecified?: boolean;
    mergedStart?: Date;
    mergedEnd?: Date;
}

const parseIfPresent = (val?: string, dateFormat?: string) => {
    if (!val || !dateFormat) {
        return undefined;
    }
    const parsed = parse(val, dateFormat, new Date());
    return isValid(parsed) ? parsed : undefined;
};

interface DestructuredSchedule {
    start?: Date;
    startTime?: Date;
    end?: Date;
    endTime?: Date;
    durationSpecified?: boolean;
    dateTimeDuration?: Measurement;
    mergedStart?: Date;
    mergedEnd?: Date;
}

export const destructureSchedule = (value?: DateRange): DestructuredSchedule | undefined => {
    if (!value) return;

    const start = parseIfPresent(value.start, API_DATE_FORMAT);
    const startTime = parseIfPresent(value.startTime, API_TIME_FORMAT);
    const end = parseIfPresent(value.end, API_DATE_FORMAT);
    const endTime = parseIfPresent(value.endTime, API_TIME_FORMAT);

    return {
        start,
        startTime,
        end,
        endTime,
        durationSpecified: value.durationSpecified,
        dateTimeDuration: value.dateTimeDuration,
        mergedStart: start && startTime ? mergeDateAndTime(start, startTime) : start,
        mergedEnd: end && endTime ? mergeDateAndTime(end, endTime) : end
    };
};

// return the date of the first of the current month in string format
export const getFirstDayOftheMonth = () => {
    const currentDate = new Date();
    const firstDayOfMonth = startOfMonth(currentDate);
    const formattedDate = format(firstDayOfMonth, "yyyy-MM-dd'T'HH:mm:ss");
    return formattedDate;
};

export const toBryntumCalTime = (time) => {
    const parsedTime = parse(time, 'HHmmss', new Date());
    const formattedTime = format(parsedTime, 'HH:mm');
    return formattedTime;
};

export const breakDateRangeIntoMonths = (start, end, unit) => {
    const buckets = [];
    let currentMonthStart = new Date(start);
    while (currentMonthStart <= end) {
        // Calculate the last day of the current month
        const currentMonthEnd = new Date(currentMonthStart);
        currentMonthEnd.setMonth(currentMonthStart.getMonth() + 1, 0); // Set to last day of the month
        unit == 'hour' && currentMonthEnd.setHours(23, 59);

        const bucketEnd = currentMonthEnd < end ? currentMonthEnd : end;

        buckets.push([new Date(currentMonthStart), bucketEnd]);

        // Move to the start of the next month
        currentMonthStart.setMonth(currentMonthStart.getMonth() + 1, 1);
        unit == 'hour' && currentMonthStart.setHours(0, 0);
    }

    // end date of the last bucket should be equal to dateRange end
    if (buckets.length > 0) {
        buckets[buckets.length - 1][1] = end;
    }

    return buckets;
};

export const adjustDateRangeToSuppressLocalTZ = (value) => {
    // Create date objects
    const minDate = new Date(value[0]);
    const maxDate = new Date(value[1]);

    // Adjust each date by the timezone offset, so that when converted using parseISO or UTC, it gets adjusted.
    value[0] = new Date(minDate.getTime() + minDate.getTimezoneOffset() * 60 * 1000);
    value[1] = new Date(maxDate.getTime() + maxDate.getTimezoneOffset() * 60 * 1000);

    // Return the adjusted values as an array
    return value;
};

export const adjustDateToSuppressLocalTZ = (dateInput) => {
    // Create date object
    const d = new Date(dateInput);

    // Adjust date by the timezone offset, so that when converted using parseISO or UTC, it gets adjusted.
    dateInput = new Date(d.getTime() + d.getTimezoneOffset() * 60 * 1000);

    // Return the adjusted value
    return dateInput;
};

export function compareRecentDate(expA, expB) {
    const parseDate = (str) => new Date(str);

    const aDate = expA?.dateRange?.end || expA?.dateRange?.start;
    const bDate = expB?.dateRange?.end || expB?.dateRange?.start;

    return parseDate(bDate).getTime() - parseDate(aDate).getTime();
}

export const isValidAPIDateByFormat = (str: string, fmt = API_DATE_FORMAT) => {
    const s = (str ?? '').trim();
    if (!/^\d+$/.test(s)) return false;
    const d = parse(s, fmt, new Date());
    return isValid(d) && format(d, fmt) === s;
};

export const validateDateRange = (
    data: Array<any>,
    validationRows: Record<string, any>,
    invalidDateFormatMsg: string,
    invalidDateRangeMsg: string
) => {
    let isInvalid = false;
    const errorMessages: string[] = [];

    for (let index = 0; index < data.length; index++) {
        const row = data[index];
        const id = row.id;
        const start = row.startDate?.trim() || '';
        const end = row.endDate?.trim() || '';
        if (!start && !end) continue;
        const startOk = isValidAPIDateByFormat(start);
        const endOk = isValidAPIDateByFormat(end);
        const rowErrors = new Set<string>();

        if (!startOk) {
            isInvalid = true;
            (validationRows[id] ??= {}).startDate ??= [];
            validationRows[id].startDate.push(invalidDateFormatMsg);
            rowErrors.add(invalidDateFormatMsg);
        }
        if (!endOk) {
            isInvalid = true;
            (validationRows[id] ??= {}).endDate ??= [];
            validationRows[id].endDate.push(invalidDateFormatMsg);
            rowErrors.add(invalidDateFormatMsg);
        }
        if (startOk && endOk) {
            if (end < start) {
                isInvalid = true;
                (validationRows[id] ??= {}).endDate ??= [];
                validationRows[id].endDate.push(invalidDateRangeMsg);
                rowErrors.add(invalidDateRangeMsg);
            }
        }

        if (rowErrors.size > 0) {
            errorMessages.push(`Row ${index + 1}: ${[...rowErrors].join(', ')}`);
        }
    }
    if (isInvalid && errorMessages.length > 0) {
        errorMessages.forEach((err) => failedToastMessage(err));
    }
};

const normalizeUnit = (raw?: string) => {
    const key = (raw ?? '').toString().trim().toLowerCase();
    return UNIT_MAP[key] ?? { period: TimePeriod.Days, unit: TIME_UNITS.day };
};

export const buildDateRangeFromUnit = (row): DateRange => {
    const { period, unit } = normalizeUnit(row.units);

    return {
        start: row.startDate || '',
        startTime: '',
        end: row.endDate || '',
        endTime: '',
        durationType: period,
        durationSpecified: true,
        dateTimeDuration: {
            value: 0,
            unit
        },
        duration: ''
    };
};

export const findDateStrDifferenceInDays = (startDateStr: string, endDateStr: string) => {
    // Parse the strings into Date objects
    const startDate = parse(startDateStr, 'yyyyMMdd', new Date());
    const endDate = parse(endDateStr, 'yyyyMMdd', new Date());

    // Calculate the difference in days
    return differenceInDays(endDate, startDate) + 1;
};

export const isStartOfDay = (d: Date): boolean =>
    d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;

export const getTimeDifferenceInHours = (start?: Date | null, end?: Date | null): number | null => {
    if (!start || !end) return null;
    const diffMs = end.getTime() - start.getTime();
    return diffMs / MS_PER_HOUR;
};

/**
 * Returns `true` when **both** `start` and `end` are present and the time span
 * between them is at least `MULTI_DAY_THRESHOLD_HOURS`.
 *
 * If either `start` or `end` is `null`/`undefined`, or if the difference
 * cannot be computed, this function returns `false`. In other words, callers
 * should:
 * - treat a `false` return value as "not a multi-day range", and
 * - handle the "missing date" scenario separately if it needs special behavior.
 */
export const isAtLeast24hoursApart = (start?: Date | null, end?: Date | null): boolean => {
    const diffHours = getTimeDifferenceInHours(start, end);
    // Note: when either start or end is nullish, diffHours will be null and we
    // deliberately return false here.
    return diffHours !== null && diffHours >= MULTI_DAY_THRESHOLD_HOURS;
};

export const formatDateYYYYMMDD = (date: Date = new Date()): string => format(date, 'yyyyMMdd');

export const formatDateForCsv = (timestamp: string | null | undefined): string => {
    if (!timestamp) return '';

    const date = parse(timestamp, API_DATE_FORMAT, new Date());

    if (!isValid(date)) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${year}${month}${day}`;
};

export const formatDateTimeToDDMMYYHHMMSS = (iso: string) => {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
        return { date: '', time: '' };
    }

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);

    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    return {
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}:${seconds}`
    };
};

const API_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
const API_DATE_REGEX = /^\d{8}$/;

export const parseServerDateToEpochMs = (value: unknown): number | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const normalizedValue = value.trim();

    if (API_DATE_REGEX.test(normalizedValue)) {
        const parsedCompactDate = parse(normalizedValue, API_DATE_FORMAT, new Date());

        if (!isValid(parsedCompactDate) || format(parsedCompactDate, API_DATE_FORMAT) !== normalizedValue) {
            return undefined;
        }

        return parsedCompactDate.getTime();
    }

    if (!API_DATE_TIME_REGEX.test(normalizedValue)) {
        return undefined;
    }

    const parsedIsoDate = parseISO(normalizedValue);
    return isValid(parsedIsoDate) ? parsedIsoDate.getTime() : undefined;
};
